import * as React from "react";
import { Box, Button, Checkbox, Chip, Link, Stack, styled, SxProps, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { useNavigate } from "react-router";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckboxBg from "../../Misc/CheckboxBg";
import { wrapperStyles } from "../CollectionCard";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";
import { CollectionLocation } from "../../../Core/Api/Types/db-collection-location";
import RequiredIndicator from "../../Misc/RequiredIndicator";
import { routePaths } from "../../../Router/router";
import { useTranslation } from "react-i18next";

export interface WishlistCollectionCardProps {
  sx?: SxProps;
  id: number;
  covers: [string, string, string, string];
  nbTitles: number;
  nbTotaleItem: number;
  title: string;
  selectMode?: boolean;
  small?: boolean;
  direction?: "column" | "row";
  fixedWidth?: number;
  clickable?: boolean;
  hidden?: boolean;
  isShared?: boolean;
  selected?: boolean;
  onSelectClick?: (collectionIndex: number) => void;
  selectedOnClick?: boolean;
  index?: number;
  location?: CollectionLocation;
  isPublished?: boolean;
  isCarousel?: boolean;
}

const WishlistCollectionCard: React.FC<WishlistCollectionCardProps> = (props) => {
  const { t } = useTranslation();
  const {
    title,
    sx = {},
    covers,
    nbTitles,
    nbTotaleItem,
    id,
    small = false,
    direction = "column",
    fixedWidth,
    clickable = true,
    hidden = false,
    isShared = false,
    selectMode = false,
    isCarousel = false,
    selected,
    onSelectClick,
    selectedOnClick,
    index,
  } = props;

  const navigate = useNavigate();

  const isDirectionRow = direction === "row";

  const handleCardClick: React.MouseEventHandler = () => {
    if (selectedOnClick) {
      if (!onSelectClick || index === undefined) {
        return;
      }

      onSelectClick(index);
      return;
    }

    navigate(`${id}`);
  };

  const handleCheckboxClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();

    if (!onSelectClick || index === undefined) {
      return;
    }

    onSelectClick(index);
  };

  const StyledBox = styled(Box)(({ theme }) => ({
    ...wrapperStyles({ theme }),
  }));

  return (
    <Stack direction={direction} alignItems={isDirectionRow ? "center" : undefined} sx={sx}>
      <CardButton onClick={handleCardClick} clickable={clickable} small={small}>
        <CardCoversSquare fixedWidth={fixedWidth} small={small} selected={selected}>
          <CardCoversWrapper small={small}>
            <CardCovers small={small}>
              {covers &&
                covers.map((cover, index) =>
                  cover !== "" ? (
                    <CardCoverWrapper
                      small={small}
                      key={index}
                      justifyContent="center"
                      alignItems="center"
                      nbtitles={nbTotaleItem}
                    >
                      <CardCover src={cover} alt="" />
                    </CardCoverWrapper>
                  ) : null
                )}
            </CardCovers>
          </CardCoversWrapper>
          {hidden && (
            <HiddenTag justifyContent="center" alignItems="center">
              <VisibilityOffIcon />
            </HiddenTag>
          )}
          {selectMode && (
            <>
              <CheckboxBg checked={selected || false} className="MuiCheckbox-Bg" sx={{ top: "17px", right: "17px" }} />
              <CardCheckbox onClick={handleCheckboxClick} checked={selected || false} />
            </>
          )}
          {isShared && <StyledLabel sx={{ mt: "90%" }} label={t("common:shared")} size="small" />}
        </CardCoversSquare>
      </CardButton>

      <Stack alignItems="center">
        <Link
          to={`${routePaths.collections.path}/${id}`}
          component={RouterLink}
          fontWeight={small ? 500 : 600}
          mt={!isDirectionRow ? (small ? 1 : 1.5) : 0}
          ml={isDirectionRow ? (small ? 1 : 1.5) : 0}
          color="text.primary"
          textAlign="center"
          variant={small ? "body1" : "subtitle2"}
          underline="hover"
        >
          <Stack direction="row" justifyContent="space-between">
            {title} {isCarousel && <RequiredIndicator color={"success.main"} sx={{ ml: 0.5, mt: 1 }} />}
          </Stack>
        </Link>
        {!small && nbTotaleItem > 0 && (
          <Typography sx={{ mt: 0.5 }} color="text.tier">
            <>
              {nbTitles > 0 && nbTitles + " movies"}
              {nbTitles > 0 && nbTotaleItem - nbTitles > 0 && ", "}
              {nbTotaleItem - nbTitles > 0 && nbTotaleItem - nbTitles + " assets"}
            </>
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default WishlistCollectionCard;

const CardButton = styled(Button, {
  shouldForwardProp: (propName) => propName !== "clickable" && propName !== "small",
})<{
  clickable: WishlistCollectionCardProps["clickable"];
  small: WishlistCollectionCardProps["small"];
}>(({ clickable, small }) => ({
  pointerEvents: clickable ? "all" : "none",
  padding: 0,
  borderRadius: small ? 6 : 12,
  background: "transparent",
  "&:hover": {
    background: "transparent",
    border: "1px solid transparent",
    ".MuiCheckbox-Bg": {
      opacity: 0.5,
    },
  },
}));

const CardCoversSquare = styled(Box, {
  shouldForwardProp: (propName) => propName !== "fixedWidth" && propName !== "small",
})<{
  fixedWidth: WishlistCollectionCardProps["fixedWidth"];
  small: WishlistCollectionCardProps["small"];
  selected?: boolean;
}>(({ theme, fixedWidth, small, selected }) => ({
  background: theme.palette.backgroundAlpha.main,
  border: `1px solid transparent`,
  borderRadius: small ? 6 : 12,
  width: fixedWidth ? `${fixedWidth}px` : "100%",
  position: "relative",
  transition: theme.transitions.create(["border", "background"], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  "&::after": {
    content: "''",
    display: "block",
    paddingBottom: "100%",
  },
  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },
  cursor: "pointer",
  ...(selected && {
    background: theme.palette.primary.alpha,
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      background: theme.palette.primary.alpha,
    },
  }),
}));

const CardCoversWrapper = styled(Box, { shouldForwardProp: (propName) => propName !== "small" })<{
  small: WishlistCollectionCardProps["small"];
}>(({ theme, small }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
  padding: small ? 2 : theme.spacing(1),
  borderRadius: small ? 4 : 6,
  overflow: "hidden",
  boxSizing: "border-box",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

export const CardCovers = styled(Box, { shouldForwardProp: (propName) => propName !== "small" })<{
  small: WishlistCollectionCardProps["small"];
}>(({ small }) => ({
  height: "100%",
  width: "100%",
  borderRadius: small ? 4 : 8,
  overflow: "hidden",
  display: "flex",
  flexWrap: "wrap",
}));

export const CardCoverWrapper = styled(Stack, {
  shouldForwardProp: (propName) => propName !== "nbTitles" && propName !== "small",
})<{ nbtitles: WishlistCollectionCardProps["nbTitles"]; small: WishlistCollectionCardProps["small"] }>(
  ({ theme, nbtitles: nbTitles, small }) => ({
    flex: nbTitles < 2 ? "1 0 100%" : "1 0 50%",
    height: nbTitles < 3 ? "100%" : "50%",
    position: "relative",
    boxSizing: "border-box",
    overflow: "hidden",
    "&:nth-of-type(1), &:nth-of-type(3)": {
      borderRight: "1px solid transparent",
    },
    "&:nth-of-type(2), &:nth-of-type(4)": {
      borderLeft: "1px solid transparent",
    },
    "&:nth-of-type(1), &:nth-of-type(2)": {
      borderBottom: "1px solid transparent",
    },
    "&:nth-of-type(3), &:nth-of-type(4)": {
      borderTop: "1px solid transparent",
    },
    "&:nth-of-type(4)": {
      ...(nbTitles &&
        nbTitles > 4 && {
          "&::after": {
            content: `"+${nbTitles - 4}"`,
            position: "absolute",
            height: "100%",
            width: "100%",
            left: 0,
            top: 0,
            backgroundImage: `radial-gradient(100% 80.74% at 100% 28.04%, rgba(${hexToRgbString(
              theme.palette.primary.main
            )}, 0.3) 0%, rgba(${hexToRgbString(
              theme.palette.primary.main
            )}, 0) 100%), linear-gradient(0deg, rgba(${hexToRgbString(
              theme.palette.background.paper
            )}, 0.84), rgba(${hexToRgbString(theme.palette.background.paper)}, 0.84))`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            ...(small ? theme.typography.body2 : theme.typography.h3),
            fontWeight: 500,
          },
        }),
    },
  })
);

export const CardCover = styled("img")(() => ({
  height: "100%",
  width: "100%",
  objectFit: "cover",
}));

const HiddenTag = styled(Stack)(({ theme }) => ({
  height: "64px",
  width: "64px",
  background: "white",
  borderRadius: "50%",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  margin: "auto",
  svg: {
    height: "32px",
    width: "32px",
    color: theme.palette.text.tier,
  },
}));

const CardCheckbox = styled(Checkbox)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

const StyledLabel = styled(Chip, { shouldForwardProp: (prop) => prop !== "type" })<{}>(({ theme }) => {
  return {
    background: theme.palette.warning.alpha,
    color: theme.palette.text.inverted,
    borderRadius: 0,
    textTransform: "uppercase",
    zIndex: 1,
    width: "fit-content",
    float: "inline-end",
    position: "relative",
    marginTop: 10,
  };
});
